// src/hooks/useVocabularyHub.js
import { useState, useMemo } from 'react';
import { playSound } from '../utils/sounds';

export function useVocabularyHub({ 
  userProgress, 
  vocabularyData, 
  onBuyContent, 
  handleSurprisePurchase, 
  mode, 
  CHEST_COST 
}) {
  // --- ESTADOS ---
  const [chestOpening, setChestOpening] = useState(false);
  const [wonItem, setWonItem] = useState(null);
  const [showChestConfirm, setShowChestConfirm] = useState(false);
  const [viewingSet, setViewingSet] = useState(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(null);
  const [xpErrorModal, setXpErrorModal] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('library');

  const currentSkin = userProgress.currentSkin || 'bribark';

  // --- LÓGICA DE NEGOCIO MEJORADA ---
  
  // ✅ 1. Contar sets completados por Módulo
  const completedByModule = useMemo(() => {
    const counts = {};
    const completedIds = userProgress.completedSets || [];
    
    completedIds.forEach(id => {
      const setData = vocabularyData.sets.find(s => s.id === id);
      if (setData && setData.ModuleID) {
        counts[setData.ModuleID] = (counts[setData.ModuleID] || 0) + 1;
      }
    });
    return counts;
  }, [userProgress.completedSets, vocabularyData.sets]);

  // ✅ 2. NUEVO: Estado del módulo seleccionado (integrado del primer hook)
  const moduleStatus = useMemo(() => {
    const moduleId = parseInt(selectedFilter);
    
    // Si no es un número (ej. "library", "Slang"), no hay bloqueo por módulos
    if (isNaN(moduleId)) return { isLocked: false };

    // Regla 0: Modo Profesor o Módulo 1 siempre abiertos
    if (userProgress.isTeacherMode || moduleId === 1) return { isLocked: false };

    // Regla 1: Si ya fue comprado (XP)
    if (userProgress.unlockedModules?.includes(moduleId)) return { isLocked: false };

    // Regla 2: Verificar progreso del módulo anterior (N-1)
    const prevModuleId = moduleId - 1;
    const completedInPrev = completedByModule[prevModuleId] || 0;
    const setsNeeded = 5;

    if (completedInPrev >= setsNeeded) return { isLocked: false };

    // Si llega aquí, está bloqueado
    return {
      isLocked: true,
      remaining: setsNeeded - completedInPrev,
      prevModule: prevModuleId,
      cost: 1000
    };
  }, [selectedFilter, userProgress.isTeacherMode, userProgress.unlockedModules, completedByModule]);

  // ✅ 3. Determinar si un módulo está bloqueado (función genérica)
  const getModuleStatus = useMemo(() => {
    return (moduleId) => {
      // Modo Profesor o Módulo 1 siempre abiertos
      if (userProgress.isTeacherMode || moduleId === 1) {
        return { locked: false };
      }

      // Si ya fue comprado por 1000 XP
      if (userProgress.unlockedModules?.includes(moduleId)) {
        return { locked: false };
      }

      // Verificar progreso del módulo anterior (N-1)
      const prevModule = moduleId - 1;
      const completedInPrev = completedByModule[prevModule] || 0;
      const needed = 5;

      if (completedInPrev >= needed) {
        return { locked: false };
      }

      return { 
        locked: true, 
        remaining: needed - completedInPrev,
        prevModule,
        cost: 1000
      };
    };
  }, [userProgress.isTeacherMode, userProgress.unlockedModules, completedByModule]);

  // ✅ 4. Versión legacy de isModuleLocked (mantiene compatibilidad)
  const isModuleLocked = (num) => {
    return getModuleStatus(num).locked;
  };

const filteredSets = useMemo(() => {
  return vocabularyData.sets.filter((set) => {
    const isOwned = set.cost === 0 || userProgress.unlockedContent?.includes(set.id);

    if (mode === 'shop') return !isOwned;

    if (selectedFilter === 'library') {
      return isOwned && (set.ModuleID == null || set.ModuleID < 1 || set.ModuleID > 10);
    }

    if (selectedFilter === 'all') return isOwned;

    if (selectedFilter.startsWith('module-')) {
      const num = parseInt(selectedFilter.split('-')[1]);
      return set.ModuleID === num && isOwned;
    }

    if (selectedFilter.startsWith('cat-')) {
      const cat = selectedFilter.split('-')[1];
      const setCategory = set.Category || 'general'; // ← único cambio ✅
      return setCategory === cat && isOwned;
    }

    if (!isNaN(parseInt(selectedFilter))) {
      return set.ModuleID === parseInt(selectedFilter) && isOwned;
    }

    return false;
  });
}, [selectedFilter, mode, userProgress.unlockedContent, vocabularyData.sets]);


  // --- MANEJADORES DE COFRE ---
  
  const handleChestClick = () => { 
    if (!chestOpening) setShowChestConfirm(true); 
  };

  const confirmChestPurchase = () => {
    setShowChestConfirm(false);
    const xp = userProgress.xp || 0;

    if (xp < CHEST_COST) {
      playSound('wrong');
      const dialogues = currentSkin.includes('dolly') ? 
        [
          "Ups… ese cofre todavía no es tuyo 💕",
          "Te falta un poquito para abrirlo ✨",
          "Un empujoncito más y lo abrimos 💖",
          "Casi casi… no te rindas 🌸",
          "Sigamos jugando y volvemos por ese cofre 😊"
        ] : [
          "Eh… ese cofre cuesta XP 😏",
          "Casi, pero no… a farmear! 🐾",
          "Sin XP no hay cofre, campeón 💪",
          "No alcanza… toca chambear un poco 😈",
          "Más XP primero, luego abrimos 😎"
        ];
      
      setXpErrorModal({
        title: "¡Te faltan XP!",
        message: dialogues[Math.floor(Math.random() * dialogues.length)],
        missingXp: Math.max(CHEST_COST - xp, 0)
      });
      return;
    }

    setChestOpening(true);
    playSound('wow');
    setTimeout(() => {
      const result = handleSurprisePurchase(CHEST_COST);
      if (result) { 
        setWonItem(result); 
        playSound('wow'); 
      } else { 
        setChestOpening(false); 
      }
    }, 1500);
  };

  // --- MANEJADOR DE SETS ---
  
  const handleSetClick = (set, isOwned) => {
    if (isOwned) { 
      setViewingSet(set); 
      return; 
    }
    
    const xp = userProgress.xp || 0;

    if (xp >= set.cost) {
      playSound('compra');
      if (onBuyContent(set.id, set.cost)) {
        setTimeout(() => playSound('wow'), 300);
        setPurchaseSuccess(set);
      }
    } else {
      playSound('wrong');
      const dialogues = currentSkin.includes('dolly') ?
        [
          "Ups… te faltan un poquito de XP 💕", 
          "Aún no alcanza, pero ya casi ✨"
        ] :
        [
          "Eh… te faltan XP 😏", 
          "Casi, pero no… a seguir dandole! 🐾"
        ];
      
      setXpErrorModal({
        title: "¡Te faltan XP!",
        message: dialogues[Math.floor(Math.random() * dialogues.length)],
        missingXp: Math.max(set.cost - xp, 0)
      });
    }
  };

  // --- RETORNO UNIFICADO ---
  return {
    states: { 
      chestOpening, 
      wonItem, 
      showChestConfirm, 
      viewingSet, 
      purchaseSuccess, 
      xpErrorModal, 
      selectedFilter, 
      currentSkin 
    },
    actions: { 
      setSelectedFilter, 
      handleChestClick, 
      confirmChestPurchase, 
      handleSetClick, 
      setXpErrorModal, 
      setWonItem, 
      setPurchaseSuccess,  
      setChestOpening,  
      setViewingSet, 
      setShowChestConfirm 
    }, 
    computed: { 
      filteredSets, 
      completedByModule,
      moduleStatus,        // ✅ NUEVO: Estado del módulo seleccionado
      getModuleStatus,     // Función genérica para cualquier módulo
      isModuleLocked       // Mantiene compatibilidad con código existente
    }
  };
}
